import React, { useState, useEffect } from 'react'
import { Button, List, Tabs, message as AntMessage } from 'antd'

import mailboxApiHelper from 'services/mailbox-api'
import { openConfirmUI } from 'popups'

import i18n from 'i18n'
import classNames from 'classnames'

import {
  confirmBase,
  isEmail,
  confirmDelete,
  // dragPopup
} from 'utils/ui'

import { getMenuIcon } from 'pages/main/data/IconHelper'

import styles from './style.module.scss'

interface ACLItem {
  id: string | number
  subject: string
  type: number
}

const openAddSenderPopup = (type: number, loadData: () => void): void => {
  const isBlocked = type === 1
  // const isAllowed = type === 2

  const title = isBlocked
    ? i18n.t('email.title.addBlockedSender')
    : i18n.t('email.title.addAllowedSender')
  // const placeholder = ''

  openConfirmUI({
    title,
    width: 380,
    isValidate: (text: string): boolean => {
      return isEmail(text) === true
    },
    okText: i18n.t('common.button.save'),
    onOk: async (text: string, closeFunc: () => void): Promise<void> => {
      console.log('onOk', text)
      try {
        const res = await mailboxApiHelper.ACL.find(text)
        console.log('res', res)
        if (res != null && res.data != null) {
          const resBlocked = res.data.type === 1
          confirmBase({
            title: resBlocked
              ? i18n.t('email.title.BlockedSenderExist')
              : i18n.t('email.title.AllowedSenderExist'),
            content: resBlocked
              ? i18n.t('email.desc.BlockedSenderExist')
              : i18n.t('email.desc.AllowedSenderExist'),
            width: 380,
            okText: i18n.t('common.button.ok'),
            cancelButtonProps: {
              className: classNames('hidden'),
            },
          })
          return
        }
      } catch (err) {
        console.log('err', err)
      }

      try {
        const res = await mailboxApiHelper.ACL.post({ type, subject: text })
        console.log('res', res)
        if (res != null) {
          loadData()
          closeFunc()
        } else {
          AntMessage.error(i18n.t('email.error.addSenderFailed'))
        }
      } catch (err: any) {
        // console.log('err', err)
        AntMessage.error(err.message)
      }
    },
  })
}

const openEditSenderPopup = (type: number, item: ACLItem, loadData: () => void): void => {
  const isBlocked = type === 1
  // const isAllowed = type === 2

  const title = isBlocked
    ? i18n.t('email.title.editBlockedSender')
    : i18n.t('email.title.editAllowedSender')
  // const placeholder = ''

  openConfirmUI({
    title,
    width: 380,
    initialText: item.subject,
    isValidate: (text: string): boolean => {
      return isEmail(text) === true
    },
    okText: i18n.t('common.button.save'),
    onOk: (text: string, closeFunc: () => void): void => {
      console.log('onOk', text)
      mailboxApiHelper.ACL.put(item.id.toString(), { type, subject: text })
        .then((res: any) => {
          console.log('res', res)
          loadData()
          closeFunc()
        })
        .catch((err: any) => {
          // console.log('err', err)
          AntMessage.error(err.message)
        })
    },
  })
}

const openDeleteSenderPopup = (type: number, item: ACLItem, loadData: () => void): void => {
  const isBlocked = type === 1

  const descInfo = { sender: item.subject }
  const title = isBlocked
    ? i18n.t('email.title.deleteBlockedSender')
    : i18n.t('email.title.deleteAllowedSender')
  const content = isBlocked
    ? i18n.t('email.desc.deleteBlockedSender', descInfo)
    : i18n.t('email.desc.deleteAllowedSender', descInfo)
  // const placeholder = ''

  confirmDelete({
    title,
    content,
    width: 380,
    okText: i18n.t('common.button.ok'),
    onOk: (closeFunc: () => void): void => {
      console.log('onOk')
      mailboxApiHelper.ACL.delete(item.id.toString())
        .then((res: any) => {
          console.log('res', res)
          loadData()
          closeFunc()
        })
        .catch((err: any) => {
          // console.log('err', err)
          AntMessage.error(err.message)
        })
    },
  })
}

interface SenderListProps {
  type: number
}

const SenderList: React.FC<SenderListProps> = (props) => {
  const { type } = props
  const [loading, setLoading] = useState<boolean>(false)
  const [data, setData] = useState<ACLItem[]>([])

  const loadData = (): void => {
    if (loading) {
      return
    }
    setLoading(true)
    mailboxApiHelper.ACL.get({ type: type != null ? type : 0 })
      .then((res: any) => {
        const results = res.data != null && Array.isArray(res.data) ? res.data : []
        setData([...results])
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    loadData()
  }, [])

  let descElem: React.ReactElement | null = null
  if (type === 1) {
    descElem = (
      <div className={classNames(styles.descWrapper)}>
        <div className={classNames(styles.desc)}>{i18n.t('email.desc.blockedSenders')}</div>
        <Button onClick={() => openAddSenderPopup(type, loadData)}>
          {i18n.t('email.button.addBlockedSender')}
        </Button>
      </div>
    )
  } else if (type === 2) {
    descElem = (
      <div className={classNames(styles.descWrapper)}>
        <div className={classNames(styles.desc)}>{i18n.t('email.desc.allowedSenders')}</div>
        <Button onClick={() => openAddSenderPopup(type, loadData)}>
          {i18n.t('email.button.addAllowedSender')}
        </Button>
      </div>
    )
  }

  return (
    <div className={classNames(styles.senderListWrapper)}>
      {descElem}
      <div className={classNames(styles.senderList)}>
        <List
          dataSource={data}
          locale={{ emptyText: <br /> }}
          renderItem={(item: ACLItem) => (
            <List.Item key={item.id}>
              <div className={classNames(styles.subject)}>{item.subject}</div>
              <div className={classNames(styles.edit)}>
                <Button
                  type="text"
                  onClick={() => openEditSenderPopup(type, item, loadData)}
                  icon={getMenuIcon('toolbar-edit', 'edit')}
                />
              </div>
              <div className={classNames(styles.delete)}>
                <Button
                  type="text"
                  onClick={() => openDeleteSenderPopup(type, item, loadData)}
                  icon={getMenuIcon('toolbar-delete', 'delete')}
                />
              </div>
            </List.Item>
          )}
        />
      </div>
    </div>
  )
}

interface ManageSendersOptions {
  title?: string
  afterClose?: () => void
}

export function openManageSenders(options?: ManageSendersOptions): void {
  const opts = options || {}

  const tmpObj: any = {
    title: opts.title || i18n.t('email.title.manageSenders'),
    innerRef: React.createRef(),
    tabsItems: [
      {
        label: i18n.t('email.tab.allowedSenders'),
        key: 'allowedSenders',
        children: <SenderList type={2} />,
      },
      {
        label: i18n.t('email.tab.blockedSenders'),
        key: 'blockedSenders',
        children: <SenderList type={1} />,
      },
    ],
  }

  tmpObj.render = (): React.ReactElement => {
    return <Tabs defaultActiveKey="allowedSenders" centered items={tmpObj.tabsItems} />
  }

  tmpObj.modalObj = confirmBase({
    wrapClassName: classNames(styles.manageSenders),
    title: tmpObj.title,
    content: tmpObj.render(),
    okText: i18n.t('common.button.close'),
    width: 640,
    maskClosable: false,
    okButtonProps: {
      className: classNames('type1', 'h38', 'w100'),
    },
    cancelButtonProps: {
      className: classNames('hidden'),
    },
    afterClose: () => {
      // tmpObj.dragPopup.removeEvent();
      if (opts.afterClose != null) {
        opts.afterClose()
      }
    },
  })

  // tmpObj.dragPopup = dragPopup(tmpObj);

  tmpObj.updateModal = (): void => {
    tmpObj.modalObj.update({
      content: tmpObj.render(),
    })
  }
}

const manageSendersPopup = {
  openManageSenders,
}

export default manageSendersPopup
